import React, { useRef } from "react"
import {
    componentStyles,
    ExpandableMarkup,
} from "@mallardbay/lib-react-components"

import ActivityCardMedia from "~components/shared/activity-card/activity-card-media"
import ActivityCardTopSection from "~components/shared/activity-card/activity-card-top-section"
import ActivityReactions from "~components/shared/activity-card/activity-reactions"
import Card from "~components/shared/card"
import {
    type ActivityFieldsFragment,
    type MediaFieldsFragment,
} from "~graphql/generated/graphql"
import { useTabletMediaQueries } from "~components/shared/todo-lib-react-components/hooks/use-media-queries"
import { useTrackNewsfeedPostInteractedWithEvent } from "~utils/event-hooks"
import Box from "~components/shared/todo-lib-react-components/box"

interface Props {
    readonly avatar?: MediaFieldsFragment | null
    readonly title: string
    readonly activity: ActivityFieldsFragment
    readonly inModal?: boolean
    readonly onClick?: (activity: ActivityFieldsFragment) => void
    readonly isInteractedWithEventTrackingEnabled?: boolean
}

const MAX_MOBILE_CARD_HEIGHT = "200px"

export default function ActivityCard({
    avatar,
    title,
    activity,
    inModal,
    onClick,
    isInteractedWithEventTrackingEnabled,
}: Props) {
    const containerRef = useRef<HTMLDivElement>(null)
    const { isLargerThanTablet } = useTabletMediaQueries()
    const shouldShowFullCard = inModal ?? isLargerThanTablet

    const styles = useStyles(shouldShowFullCard)

    const markupMaxLength = getMaxMarkupLength({ inModal, isLargerThanTablet })

    const cardHeight = shouldShowFullCard ? undefined : MAX_MOBILE_CARD_HEIGHT

    useTrackNewsfeedPostInteractedWithEvent({
        activity,
        isEnabled: isInteractedWithEventTrackingEnabled,
        divElementRef: containerRef,
    })

    return (
        <Card height={cardHeight} onClick={() => onClick?.(activity)}>
            <Box ref={containerRef} style={styles.container}>
                <ActivityCardTopSection
                    avatar={avatar}
                    title={title}
                    time={activity.time}
                />
                <Box style={styles.content}>
                    <Box style={styles.markup}>
                        <ExpandableMarkup
                            markup={activity.content}
                            maxLength={markupMaxLength}
                            isCompact
                            hideShowMoreButton={!inModal}
                        />
                    </Box>
                    <ActivityCardMedia
                        activity={activity}
                        shouldShowFullCard={shouldShowFullCard}
                    />
                </Box>
                {shouldShowFullCard && (
                    <ActivityReactions activity={activity} />
                )}
            </Box>
        </Card>
    )
}

function getMaxMarkupLength({
    inModal,
    isLargerThanTablet,
}: {
    inModal?: boolean
    isLargerThanTablet?: boolean
}) {
    if (inModal) return 400

    return isLargerThanTablet ? 200 : 150
}

function useStyles(shouldShowFullCard?: boolean) {
    const mobileFlexGrow = shouldShowFullCard ? "unset" : 4
    const mobileFlexBasis = shouldShowFullCard ? "unset" : 0

    return componentStyles({
        container: {
            display: "flex",
            flexDirection: "column",
            gap: "16px",
        },
        markup: {
            flexGrow: { base: mobileFlexGrow, md: "unset" },
            flexBasis: { base: mobileFlexBasis, md: "unset" },
            lineHeight: "16px",
            overflow: "hidden",
            textOverflow: "ellipsis",
        },
        content: {
            display: "flex",
            flexDirection: shouldShowFullCard ? "column" : "row-reverse",
            gap: "16px",
        },
    })
}
